<template>
  <div class="admin-dashboard-wrapper">
    <div class="min-h-screen bg-white">
      <div v-if="isTranslating" class="fixed inset-0 z-[9999] flex items-center justify-center">
        <Loader size="large" />
      </div>
      <div class="flex" data-admin-translate-root :class="{ 'opacity-0 pointer-events-none': isTranslating }"
        style="transition: opacity 0.2s;">
        <aside class="hidden lg:block w-64 bg-green-700 border-r border-green-800 min-h-screen sticky top-0">
          <div class="p-6 border-b border-green-800">
            <div class="text-xl md:text-2xl font-marketing text-white">Admin Dashboard</div>
          </div>
          <div class="p-4 border-b border-green-800">
            <div class="flex items-center border-b-2 border-green-200 pb-0">
              <input v-model="searchQuery" type="text" placeholder="Search menu items..."
                class="flex-1 bg-transparent text-white placeholder-green-200 border-none outline-none text-base font-body mr-4" />
              <div class="flex items-center">
                <button v-if="searchQuery.trim()" type="button" @click="clearSearch"
                  class="p-2 text-white hover:text-green-200 transition-colors flex-shrink-0" aria-label="Clear search">
                  <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
                <button type="button" @click="handleSearch"
                  class="p-2 bg-white text-green-700 hover:bg-green-50 transition-colors flex-shrink-0"
                  aria-label="Search">
                  <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                      d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                </button>
              </div>
            </div>
          </div>
          <nav class="p-4 space-y-2">
            <button v-for="section in filteredSections" :key="section.id" @click="selectSection(section.id)" :class="[
              'w-full text-left px-4 py-3 rounded-md transition-colors font-marketing text-sm md:text-base text-white',
              currentSection === section.id
                ? 'bg-green-600'
                : 'hover:bg-green-600'
            ]">
              {{ section.name }}
            </button>
            <p v-if="filteredSections.length === 0" class="text-sm text-green-200 text-center py-4 font-marketing">
              No menu items found
            </p>
          </nav>
        </aside>

        <main
          class="admin-content-area flex-1 min-w-0 bg-white min-h-screen px-4 sm:px-6 lg:pl-12 lg:pr-8 lg:pt-8"
          :style="mobileMainStyle">
          <div
            ref="mobileHeader"
            class="lg:!hidden fixed left-0 right-0 z-[70] bg-white border-b border-gray-200 px-4 sm:px-6 py-3 pointer-events-auto transition-opacity"
            :class="{ 'opacity-0 pointer-events-none': showMobileNav || globalMobileMenuOpen }"
            :style="{ top: `${mobileHeaderOffset}px` }">
            <div class="flex items-center justify-between gap-3">
              <button type="button" @click="showMobileNav = true"
                class="inline-flex items-center justify-center rounded-md border border-gray-300 bg-white p-2 text-gray-900 hover:bg-gray-50"
                aria-label="Open admin menu">
                <svg class="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              </button>
              <div class="min-w-0">
                <p class="text-xs text-gray-500 font-sans">Admin</p>
                <p class="text-base font-marketing font-semibold text-gray-900 truncate">{{ currentSectionName }}</p>
              </div>
              <div class="w-9" />
            </div>
          </div>

          <AdminSlider v-if="currentSection === 'slider'" />
          <AdminSectionManager v-else-if="currentSection === 'bestsellers'" section="bestsellers" />
          <AdminSectionManager v-else-if="currentSection === 'coming-soon'" section="coming-soon" />
          <AdminSectionManager v-else-if="currentSection === 'recommended-reads'" section="recommended-reads" />
          <AdminBooks v-else-if="currentSection === 'books'" />
          <AdminAuthors v-else-if="currentSection === 'authors'" />
          <AdminEvents v-else-if="currentSection === 'events'" />
          <AdminSubmissions v-else-if="currentSection === 'submissions'" />
          <AdminPress v-else-if="currentSection === 'press'" />
          <AdminCareers v-else-if="currentSection === 'careers'" />
          <AdminAbout v-else-if="currentSection === 'about'" />
          <AdminTeam v-else-if="currentSection === 'team'" />
          <AdminPartners v-else-if="currentSection === 'partners'" />
          <AdminPageDescription v-else-if="currentSection === 'products'" page-slug="products"
            page-display-name="Products and Services" />
          <AdminFAQs v-else-if="currentSection === 'faq'" />
          <AdminPageDescription v-else-if="currentSection === 'guidelines'" page-slug="guidelines"
            page-display-name="Our Guidelines" />
          <AdminPageDescription v-else-if="currentSection === 'terms'" page-slug="terms"
            page-display-name="Terms of Use" />
          <AdminPrivacy v-else-if="currentSection === 'privacy'" />
          <AdminPageDescription v-else-if="currentSection === 'cookies'" page-slug="cookies"
            page-display-name="Cookies" />
          <AdminPageDescription v-else-if="currentSection === 'contact'" page-slug="contact"
            page-display-name="Contact Us" />
          <AdminPageDescription v-else-if="currentSection === 'donate'" page-slug="donate" page-display-name="Donate" />
          <AdminContact v-else-if="currentSection === 'contact-submissions'" />
          <AdminContactInfo v-else-if="currentSection === 'contact-info'" />
          <AdminDeliveryOptions v-else-if="currentSection === 'delivery-options'" />
          <AdminAnalytics v-else-if="currentSection === 'analytics'" />
          <AdminAccounts v-else-if="currentSection === 'accounts'" />
          <AdminNewsletter v-else-if="currentSection === 'newsletter'" />
          <AdminBanners v-else-if="currentSection === 'banners'" @preview-updated="handleBannerPreviewUpdate"
            @banner-preview-updated="$emit('banner-preview-updated', $event)" />
          <AdminSales v-else-if="currentSection === 'sales'" />
          <AdminCareerApplications v-else-if="currentSection === 'career-applications'" />
          <AdminHealth v-else-if="currentSection === 'health'" />
        </main>
      </div>

      <div v-if="showMobileNav" class="lg:!hidden fixed inset-0 z-[130]">
        <div class="absolute inset-0 bg-black/50" @click="showMobileNav = false"></div>
        <div data-admin-mobile-nav
          class="absolute inset-y-0 left-0 w-80 max-w-[85vw] bg-white border-r border-gray-200 overflow-y-auto">
          <div class="p-4 border-b border-gray-200 flex items-center justify-between gap-3">
            <h2 class="text-lg font-marketing font-semibold text-gray-900">Menu</h2>
            <button type="button" @click="showMobileNav = false"
              class="inline-flex items-center justify-center rounded-md p-2 text-gray-700 hover:text-gray-900 hover:bg-gray-50"
              aria-label="Close admin menu">
              <svg class="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <div class="p-4 border-b border-gray-200">
            <div class="flex items-center border-b-2 border-gray-300 pb-0">
              <input v-model="searchQuery" type="text" placeholder="Search menu items..."
                class="flex-1 bg-transparent text-primary placeholder-gray-400 border-none outline-none text-base font-body mr-4" />
              <div class="flex items-center">
                <button v-if="searchQuery.trim()" type="button" @click="clearSearch"
                  class="p-2 text-gray-700 hover:text-gray-900 transition-colors flex-shrink-0"
                  aria-label="Clear search">
                  <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
                <button type="button" @click="handleSearch"
                  class="p-2 bg-green-600 text-white hover:bg-green-700 transition-colors flex-shrink-0"
                  aria-label="Search">
                  <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                      d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                </button>
              </div>
            </div>
          </div>

          <nav class="p-4 space-y-2">
            <button v-for="section in filteredSections" :key="section.id" @click="selectSection(section.id)" :class="[
              'w-full text-left px-4 py-3 rounded-md transition-colors font-marketing text-gray-900',
              currentSection === section.id
                ? 'bg-creme-100 border-l-4 border-green-700 pl-3 font-semibold'
                : 'hover:bg-gray-50'
            ]">
              {{ section.name }}
            </button>
            <p v-if="filteredSections.length === 0" class="text-sm text-gray-500 text-center py-4 font-marketing">
              No menu items found
            </p>
          </nav>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, computed, watch, nextTick, onUnmounted } from 'vue'
import { authService } from '../services/auth'
import { translationService } from '../services/translation'
import AdminBooks from './admin/AdminBooks.vue'
import AdminAuthors from './admin/AdminAuthors.vue'
import AdminSlider from './admin/AdminSlider.vue'
import AdminSectionManager from './admin/AdminSectionManager.vue'
import AdminDeliveryOptions from './admin/AdminDeliveryOptions.vue'
import AdminEvents from './admin/AdminEvents.vue'
import AdminAnalytics from './admin/AdminAnalytics.vue'
import AdminAccounts from './admin/AdminAccounts.vue'
import AdminNewsletter from './admin/AdminNewsletter.vue'
import AdminCareers from './admin/AdminCareers.vue'
import AdminBanners from './admin/AdminBanners.vue'
import AdminSales from './admin/AdminSales.vue'
import AdminContact from './admin/AdminContact.vue'
import AdminContactInfo from './admin/AdminContactInfo.vue'
import AdminSubmissions from './admin/AdminSubmissions.vue'
import AdminPress from './admin/AdminPress.vue'
import AdminCareerApplications from './admin/AdminCareerApplications.vue'
import AdminPageDescription from './admin/AdminPageDescription.vue'
import AdminPrivacy from './admin/AdminPrivacy.vue'
import AdminAbout from './admin/AdminAbout.vue'
import AdminTeam from './admin/AdminTeam.vue'
import AdminPartners from './admin/AdminPartners.vue'
import AdminFAQs from './admin/AdminFAQs.vue'
import AdminHealth from './admin/AdminHealth.vue'
import Loader from './Loader.vue'

const props = defineProps({
  adminBannerPreview: {
    type: Object,
    default: null
  },
  initialSection: {
    type: String,
    default: null
  }
})

const currentSection = ref('slider')
const searchQuery = ref('')
const isTranslating = ref(false)
const showMobileNav = ref(false)
const globalMobileMenuOpen = ref(false)
const mobileHeaderOffset = ref(0)
const mobileHeaderHeight = ref(0)
const mobileHeader = ref(null)
const isDesktop = ref(false)

const emit = defineEmits(['banner-preview-updated'])

const handleBannerPreviewUpdate = (bannerData) => {
  emit('banner-preview-updated', bannerData)
}

const waitForDOMUpdate = () => {
  return new Promise(resolve => {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        setTimeout(resolve, 50)
      })
    })
  })
}

const updateMobileHeaderOffset = () => {
  const navbar = document.querySelector('[data-global-navbar]')
  if (navbar instanceof HTMLElement) {
    const navbarBottom = navbar.getBoundingClientRect().bottom
    mobileHeaderOffset.value = Math.max(0, navbarBottom)
  } else {
    mobileHeaderOffset.value = 0
  }
  mobileHeaderHeight.value = mobileHeader.value instanceof HTMLElement ? mobileHeader.value.offsetHeight : 0
  isDesktop.value = window.innerWidth >= 1024
}

const onKeyDown = (e) => {
  if (e.key === 'Escape') showMobileNav.value = false
}

const handleGlobalMobileMenuChange = (event) => {
  globalMobileMenuOpen.value = Boolean(event.detail?.isOpen)
}

const mobileMainStyle = computed(() => {
  if (isDesktop.value) return {}

  return {
    paddingTop: `${mobileHeaderOffset.value + mobileHeaderHeight.value + 16}px`
  }
})

const translateAdminContent = async () => {
  const currentLang = translationService.getLanguage()
  if (currentLang === 'en') {
    isTranslating.value = false
    return
  }

  if (isTranslating.value) return

  isTranslating.value = true

  const translationTimeout = setTimeout(() => {
    console.warn('Translation timeout, showing content anyway')
    isTranslating.value = false
  }, 5000)

  try {
    await nextTick()
    await waitForDOMUpdate()

    const root = document.querySelector('[data-admin-translate-root]')
    if (root) {
      try {
        await Promise.race([
          translationService.translateElement(root),
          new Promise((_, reject) => setTimeout(() => reject(new Error('Translation timeout')), 4000))
        ])
      } catch (translationError) {
        console.warn('Translation failed, showing content anyway:', translationError)
      }
      await nextTick()
      await waitForDOMUpdate()
    }
  } catch (error) {
    console.error('Error translating admin content:', error)
  } finally {
    clearTimeout(translationTimeout)
    await nextTick()
    await waitForDOMUpdate()
    await new Promise(resolve => requestAnimationFrame(() => requestAnimationFrame(resolve)))
    isTranslating.value = false
  }
}

watch(() => props.initialSection, (newSection) => {
  if (newSection) {
    const validSection = sections.find(s => s.id === newSection)
    if (validSection && currentSection.value !== newSection) {
      currentSection.value = newSection
    }
  } else if (currentSection.value !== 'slider') {
    currentSection.value = 'slider'
  }
})

watch(currentSection, async (newSection, oldSection) => {
  if (oldSection && newSection !== oldSection) {
    // Scroll to top with both smooth and instant fallback
    window.scrollTo({ top: 0, behavior: 'smooth' })
    // Immediate fallback for mobile and older browsers
    setTimeout(() => {
      if (window.scrollY !== 0) {
        document.documentElement.scrollTop = 0
        document.body.scrollTop = 0
      }
    }, 0)
    isTranslating.value = false
    const currentLang = translationService.getLanguage()
    if (currentLang !== 'en') {
      await nextTick()
      await waitForDOMUpdate()
      await new Promise(resolve => setTimeout(resolve, 100))
      try {
        await translateAdminContent()
      } catch (error) {
        console.error('Error translating on section change:', error)
        isTranslating.value = false
      }
    }
  }
})

watch(showMobileNav, async (isOpen) => {
  await nextTick()
  updateMobileHeaderOffset()

  if (!isOpen) return
  const currentLang = translationService.getLanguage()
  if (currentLang === 'en') return

  await nextTick()
  await waitForDOMUpdate()

  const drawer = document.querySelector('[data-admin-mobile-nav]')
  if (drawer) {
    try {
      await translationService.translateElement(drawer)
    } catch (error) {
      console.warn('Error translating admin drawer:', error)
    }
  }
})

onMounted(async () => {
  globalMobileMenuOpen.value = document.documentElement.dataset.globalMobileMenuOpen === 'true'
  updateMobileHeaderOffset()
  window.addEventListener('resize', updateMobileHeaderOffset)
  window.addEventListener('scroll', updateMobileHeaderOffset, { passive: true })
  window.addEventListener('global-mobile-menu-change', handleGlobalMobileMenuChange)

  const user = await authService.getCurrentUser()
  if (!user || !user.is_admin) {
    window.location.href = '/'
    return
  }

  if (props.initialSection) {
    const validSection = sections.find(s => s.id === props.initialSection)
    if (validSection) {
      currentSection.value = props.initialSection
    }
  }

  const currentLang = translationService.getLanguage()
  if (currentLang !== 'en') {
    try {
      await translationService.preTranslateCommonStrings(currentLang)
    } catch (error) {
      console.warn('Pre-translation failed, continuing anyway:', error)
    }
  }

  await nextTick()
  await waitForDOMUpdate()
  try {
    await translateAdminContent()
  } catch (error) {
    console.error('Error translating on mount:', error)
    isTranslating.value = false
  }

  window.addEventListener('keydown', onKeyDown)
})

onUnmounted(() => {
  window.removeEventListener('resize', updateMobileHeaderOffset)
  window.removeEventListener('scroll', updateMobileHeaderOffset)
  window.removeEventListener('keydown', onKeyDown)
  window.removeEventListener('global-mobile-menu-change', handleGlobalMobileMenuChange)
})

const sections = [
  { id: 'slider', name: 'Home' },
  { id: 'bestsellers', name: 'Bestsellers' },
  { id: 'coming-soon', name: 'Coming Soon' },
  { id: 'recommended-reads', name: 'Recommended Reads' },
  { id: 'books', name: 'Books' },
  { id: 'authors', name: 'Authors' },
  { id: 'events', name: 'Events' },
  { id: 'submissions', name: 'Manuscript Submissions' },
  { id: 'press', name: 'Press' },
  { id: 'careers', name: 'Careers' },
  { id: 'about', name: 'About Us' },
  { id: 'team', name: 'Team' },
  { id: 'partners', name: 'Partners' },
  { id: 'products', name: 'Products and Services' },
  { id: 'faq', name: 'FAQ' },
  { id: 'guidelines', name: 'Our Guidelines' },
  { id: 'terms', name: 'Terms of Use' },
  { id: 'privacy', name: 'Privacy Policy' },
  { id: 'cookies', name: 'Cookies' },
  { id: 'contact', name: 'Contact Us' },
  { id: 'donate', name: 'Donate' },
  { id: 'contact-submissions', name: 'Contact Submissions' },
  { id: 'contact-info', name: 'Contact Information' },
  { id: 'delivery-options', name: 'Delivery Options' },
  { id: 'banners', name: 'Promotional Banners' },
  { id: 'analytics', name: 'Analytics' },
  { id: 'accounts', name: 'Accounts' },
  { id: 'newsletter', name: 'Newsletter' },
  { id: 'sales', name: 'Sales' },
  { id: 'career-applications', name: 'Career Applications' },
  { id: 'health', name: 'Health' }
]

const handleSearch = () => {
  // Search is handled reactively by filteredSections computed property
}

const clearSearch = () => {
  searchQuery.value = ''
}

const selectSection = (sectionId) => {
  currentSection.value = sectionId
  const newPath = sectionId === 'slider' ? '/admin' : `/admin/${sectionId}`
  window.history.pushState({}, '', newPath)
  showMobileNav.value = false

  // Ensure scroll to top on section change
  window.scrollTo({ top: 0, behavior: 'smooth' })
  // Immediate fallback
  setTimeout(() => {
    if (window.scrollY !== 0) {
      document.documentElement.scrollTop = 0
      document.body.scrollTop = 0
    }
  }, 0)
}

const filteredSections = computed(() => {
  if (!searchQuery.value.trim()) {
    return sections
  }
  const query = searchQuery.value.toLowerCase().trim()
  return sections.filter(section =>
    section.name.toLowerCase().includes(query) ||
    section.id.toLowerCase().includes(query)
  )
})

const currentSectionName = computed(() => {
  const match = sections.find(s => s.id === currentSection.value)
  return match?.name || 'Dashboard'
})
</script>

<style scoped>
.admin-dashboard-wrapper {
  position: relative;
}

.admin-content-area {
  display: block;
}

.admin-content-area>* {
  width: 100%;
  display: block;
}


.admin-content-area>* :deep(.bg-white.rounded-lg:not(.admin-modal-content)) {
  background-color: transparent !important;
  box-shadow: none !important;
  border: 1px solid #e5e7eb;
}

.admin-content-area>* :deep(.bg-white.rounded-lg.shadow-md:not(.admin-modal-content)),
.admin-content-area>* :deep(.bg-white.rounded-lg.shadow-sm:not(.admin-modal-content)) {
  background-color: transparent !important;
  box-shadow: none !important;
  border: 1px solid #e5e7eb;
}

.admin-content-area :deep(label) {
  color: #111111;
  font-weight: 600;
}

.admin-content-area :deep(input[type="text"]),
.admin-content-area :deep(input[type="number"]),
.admin-content-area :deep(input[type="date"]),
.admin-content-area :deep(input[type="time"]),
.admin-content-area :deep(input[type="url"]),
.admin-content-area :deep(input[type="email"]),
.admin-content-area :deep(textarea),
.admin-content-area :deep(select) {
  border-color: #7C898B !important;
  border-width: 1px !important;
}

.admin-content-area :deep(input[type="text"]:focus),
.admin-content-area :deep(input[type="number"]:focus),
.admin-content-area :deep(input[type="date"]:focus),
.admin-content-area :deep(input[type="time"]:focus),
.admin-content-area :deep(input[type="url"]:focus),
.admin-content-area :deep(input[type="email"]:focus),
.admin-content-area :deep(textarea:focus),
.admin-content-area :deep(select:focus) {
  border-color: #2D4D31 !important;
  outline: none !important;
  box-shadow: 0 0 0 2px rgba(45, 77, 49, 0.1) !important;
}

.admin-content-area :deep(.ql-editor) {
  background-color: #FFFFFF !important;
}

.admin-content-area :deep(.ql-container) {
  background-color: #FFFFFF !important;
}

.admin-content-area :deep(.ql-toolbar) {
  background-color: #FFFFFF !important;
}

.admin-content-area :deep(input[type="checkbox"]) {
  background-color: #FFFFFF !important;
  appearance: checkbox;
  -webkit-appearance: checkbox;
  -moz-appearance: checkbox;
}

.admin-content-area :deep(input[type="checkbox"]:checked) {
  background-color: #FFFFFF !important;
  accent-color: #2D4D31;
}

.admin-content-area :deep(input[type="checkbox"]),
.admin-content-area :deep(input[type="radio"]) {
  width: 1rem !important;
  height: 1rem !important;
  background-color: white !important;
  border: 2px solid #7C898B !important;
  border-radius: 0.25rem !important;
  cursor: pointer !important;
  accent-color: #2D4D31 !important;
}

.admin-content-area :deep(input[type="checkbox"]:focus),
.admin-content-area :deep(input[type="radio"]:focus) {
  outline: none !important;
  border-color: #2D4D31 !important;
  box-shadow: 0 0 0 3px rgba(45, 77, 49, 0.2) !important;
}

.admin-content-area :deep(input[type="checkbox"]:checked),
.admin-content-area :deep(input[type="radio"]:checked) {
  background-color: #2D4D31 !important;
  border-color: #2D4D31 !important;
  accent-color: #2D4D31 !important;
}

.admin-content-area :deep(input[type="checkbox"]:checked::before),
.admin-content-area :deep(input[type="radio"]:checked::before) {
  background-color: white !important;
}

aside nav button {
  color: white !important;
}
</style>
